import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { jobApplicationService } from '../../services/api';
import './Steps.css';

const StepConfirmation = ({
  formData,
  onBack,
  onSubmitSuccess,
  isSubmitting,
  setIsSubmitting,
  setError,
  error,
}) => {
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const handleSubmit = async () => {
    if (!acceptedTerms || !acceptedPrivacy) {
      setError('Debes aceptar los términos y la política de privacidad');
      return;
    }

    if (!formData.photo) {
      setError('Falta tu foto tipo carné. Vuelve al paso "Foto".');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    const applicationData = {
      applicant_type: 'driver',
      first_name: formData.first_name,
      last_name: formData.last_name,
      national_id: formData.national_id,
      phone: formData.phone,
      email: formData.email,
      city_id: formData.city_id,
    };

    // Documentos a subir (ya validados en el paso anterior)
    const documentsToUpload = {};
    if (formData.documents.national_id?.front && formData.documents.national_id?.back) {
      documentsToUpload.national_id = {
        front: formData.documents.national_id.front,
        back: formData.documents.national_id.back,
      };
    }
    if (formData.documents.license?.front && formData.documents.license?.back) {
      documentsToUpload.license = {
        front: formData.documents.license.front,
        back: formData.documents.license.back,
      };
    }
    if (formData.documents.registration?.front && formData.documents.registration?.back) {
      documentsToUpload.registration = {
        front: formData.documents.registration.front,
        back: formData.documents.registration.back,
      };
    }

    // Un intento = crear + subir documentos + subir foto. Si la subida falla,
    // se hace ROLLBACK (borra la solicitud incompleta, liberando cédula/email)
    // y se REINTENTA el ciclo completo. Así nunca queda una solicitud huérfana
    // ni parcial.
    const attempt = async () => {
      const tokenResponse = await jobApplicationService.getToken();
      const token = tokenResponse.data?.token ?? tokenResponse.token;

      const createResponse = await jobApplicationService.create(applicationData, token);
      if (!createResponse.success) {
        const e = new Error(createResponse.message || 'No se pudo crear la solicitud');
        e.fatal = true;
        e.userMessage = createResponse.message;
        throw e;
      }
      const applicationId = createResponse.data.id;

      try {
        if (Object.keys(documentsToUpload).length > 0) {
          await jobApplicationService.uploadDocuments(applicationId, documentsToUpload, token);
        }
        if (formData.photo) {
          await jobApplicationService.uploadPhoto(applicationId, formData.photo, token);
        }
        return applicationId;
      } catch (uploadErr) {
        // Rollback: borrar la solicitud incompleta para no dejarla huérfana.
        try {
          await jobApplicationService.deleteApplication(
            applicationId,
            { national_id: applicationData.national_id, email: applicationData.email },
            token
          );
        } catch (rbErr) {
          console.error('No se pudo hacer rollback de la solicitud:', rbErr);
        }
        throw uploadErr;
      }
    };

    const MAX_ATTEMPTS = 2;
    let lastError = null;
    for (let i = 0; i < MAX_ATTEMPTS; i++) {
      try {
        const applicationId = await attempt();
        setSubmitSuccess(true);
        onSubmitSuccess(applicationId);
        setIsSubmitting(false);
        return;
      } catch (err) {
        const status = err.response?.status;
        const msg = err.userMessage || err.response?.data?.message || '';
        const isDuplicate = status === 409 || /existe|already/i.test(msg);
        if (err.fatal || isDuplicate) {
          setError(isDuplicate
            ? 'Ya existe una solicitud con esta cédula o correo electrónico'
            : (msg || 'Ocurrió un error al enviar tu solicitud.'));
          setIsSubmitting(false);
          return;
        }
        console.error(`Intento ${i + 1} de ${MAX_ATTEMPTS} falló:`, err);
        lastError = err;
      }
    }

    console.error('Error final al enviar solicitud:', lastError);
    setError('No pudimos subir tus documentos. Revisa tu conexión e intenta de nuevo.');
    setIsSubmitting(false);
  };

  if (submitSuccess) {
    return (
      <div className="step-container step-success">
        <div className="success-animation">
          <div className="success-icon">🎉</div>
          <div className="success-confetti">
            <span>🎊</span>
            <span>✨</span>
            <span>🌟</span>
            <span>🎊</span>
          </div>
        </div>

        <h2 className="step-title success-title">
          ¡Solicitud Enviada!
        </h2>

        <p className="step-subtitle">
          Tu solicitud ha sido recibida exitosamente. Revisaremos tu información
          y te contactaremos pronto.
        </p>

        <div className="next-steps">
          <h3>¿Qué sigue?</h3>
          <div className="next-steps-list">
            <div className="next-step-item">
              <span className="step-number">1</span>
              <p>Revisaremos tus documentos (1-3 días hábiles)</p>
            </div>
            <div className="next-step-item">
              <span className="step-number">2</span>
              <p>Te contactaremos por WhatsApp o correo</p>
            </div>
            <div className="next-step-item">
              <span className="step-number">3</span>
              <p>¡Descarga la app y comienza a ganar!</p>
            </div>
          </div>
        </div>

        <Link to="/" className="btn-primary btn-large">
          Volver al Inicio
        </Link>
      </div>
    );
  }

  return (
    <div className="step-container">
      <div className="step-header">
        <span className="step-icon">✅</span>
        <h2 className="step-title">Confirmar Solicitud</h2>
        <p className="step-subtitle">
          Revisa tu información antes de enviar
        </p>
      </div>

      <div className="summary-section">
        <div className="summary-card">
          <h3>
            <span>👤</span> Datos Personales
          </h3>
          <div className="summary-row">
            <span>Nombre:</span>
            <strong>{formData.first_name} {formData.last_name}</strong>
          </div>
          <div className="summary-row">
            <span>Cédula:</span>
            <strong>{formData.national_id}</strong>
          </div>
        </div>

        <div className="summary-card">
          <h3>
            <span>📱</span> Contacto
          </h3>
          <div className="summary-row">
            <span>Teléfono:</span>
            <strong>{formData.phone}</strong>
          </div>
          <div className="summary-row">
            <span>Email:</span>
            <strong>{formData.email}</strong>
          </div>
        </div>

        <div className="summary-card">
          <h3>
            <span>🪪</span> Foto tipo carné
          </h3>
          <div className="summary-row">
            <span>Foto:</span>
            <strong className={formData.photo ? 'uploaded' : 'missing'}>
              {formData.photo ? '✓ Cargada' : '✗ Falta'}
            </strong>
          </div>
        </div>

        <div className="summary-card">
          <h3>
            <span>📄</span> Documentos Validados
          </h3>
          <div className="summary-row">
            <span>Cédula:</span>
            <strong className={formData.documents.national_id?.validated ? 'uploaded' : 'missing'}>
              {formData.documents.national_id?.validated ? '✓ Validada' : '✗ Sin validar'}
            </strong>
          </div>
          <div className="summary-row">
            <span>Licencia:</span>
            <strong className={formData.documents.license?.validated ? 'uploaded' : 'missing'}>
              {formData.documents.license?.validated ? '✓ Validada' : '✗ Sin validar'}
            </strong>
          </div>
          <div className="summary-row">
            <span>Matrícula:</span>
            <strong className={formData.documents.registration?.validated ? 'uploaded' : 'missing'}>
              {formData.documents.registration?.validated ? '✓ Validada' : '✗ Sin validar'}
            </strong>
          </div>
        </div>
      </div>

      <div className="terms-section">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={acceptedTerms}
            onChange={(e) => setAcceptedTerms(e.target.checked)}
          />
          <span className="checkmark"></span>
          <span>
            Acepto los{' '}
            <Link to="/terms" target="_blank">
              Términos y Condiciones
            </Link>
          </span>
        </label>

        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={acceptedPrivacy}
            onChange={(e) => setAcceptedPrivacy(e.target.checked)}
          />
          <span className="checkmark"></span>
          <span>
            Acepto la{' '}
            <Link to="/privacy" target="_blank">
              Política de Privacidad
            </Link>
          </span>
        </label>
      </div>

      {error && (
        <div className="error-banner">
          <span className="error-icon">⚠️</span>
          <span>{error}</span>
        </div>
      )}

      {/* Loading overlay mientras se envía */}
      {isSubmitting && (
        <div className="submit-loading-overlay">
          <div className="submit-loading-content">
            <div className="submit-spinner-large"></div>
            <h3>Enviando tu solicitud</h3>
            <p>Estamos subiendo tus documentos y registrando tu información.</p>
            <p className="submit-loading-wait">Por favor espera, esto puede tomar unos segundos...</p>
          </div>
        </div>
      )}

      <div className="step-actions">
        <button
          type="button"
          className="btn-secondary"
          onClick={onBack}
          disabled={isSubmitting}
        >
          <span className="btn-arrow">←</span>
          Atrás
        </button>
        <button
          type="button"
          className="btn-primary btn-submit"
          onClick={handleSubmit}
          disabled={isSubmitting || !acceptedTerms || !acceptedPrivacy}
        >
          {isSubmitting ? (
            <>
              <span className="spinner"></span>
              Enviando...
            </>
          ) : (
            <>
              Enviar Solicitud
              <span className="btn-arrow">→</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default StepConfirmation;
